import { useState, useMemo, useEffect } from 'react';
import { Search, X, Asterisk, ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { CRMField } from '@/types/saas';

interface FieldAddDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (field: CRMField) => void;
  availableFields: CRMField[];
  /** Field API names already added (to gray them out) */
  existingFieldNames: string[];
}

export function FieldAddDialog({
  open,
  onClose,
  onSelect,
  availableFields,
  existingFieldNames,
}: FieldAddDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Reset search when dialog opens
  useEffect(() => {
    if (open) setSearchQuery('');
  }, [open]);

  const { requiredFields, optionalFields } = useMemo(() => {
    const required: CRMField[] = [];
    const optional: CRMField[] = [];
    // Some CRMs return the same API name twice (Freshsales repeats city / state /
    // zipcode / country / name for the lead and its company section). Rendering
    // both gives two rows with the same React key, which breaks reconciliation —
    // the list then fails to update when the search filters it. Keep the first.
    const seen = new Set<string>();

    availableFields.forEach((field) => {
      if (field.readOnly) return; // Skip read-only fields
      if (seen.has(field.apiName)) return;
      seen.add(field.apiName);
      if (field.required) {
        required.push(field);
      } else {
        optional.push(field);
      }
    });

    // Sort optional alphabetically
    optional.sort((a, b) => (a.displayLabel || '').localeCompare(b.displayLabel || ''));

    return { requiredFields: required, optionalFields: optional };
  }, [availableFields]);

  const allFields = [...requiredFields, ...optionalFields];

  const filteredFields = useMemo(() => {
    const lower = searchQuery.trim().toLowerCase();
    if (!lower) return allFields;
    // Null-safe: a CRM can omit label or type for a field, and one throw here
    // would take down the whole dialog mid-search.
    return allFields.filter((f) =>
      [f.displayLabel, f.apiName, f.dataType].some((v) =>
        (v || '').toLowerCase().includes(lower)
      )
    );
  }, [allFields, searchQuery]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="mx-4 flex max-h-[70vh] w-full max-w-md flex-col rounded-xl border border-border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Add Field</h3>
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="relative border-b border-border px-4 py-2">
          <Search className="absolute left-6 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, API name, or type..."
            className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
          />
        </div>

        {/* Field list */}
        <div className="flex-1 overflow-y-auto p-2">
          {filteredFields.length === 0 && (
            <p className="py-8 text-center text-xs text-muted-foreground">
              No fields match your search
            </p>
          )}

          <div className="space-y-0.5">
            {filteredFields.map((field) => {
              const isAlreadyAdded = existingFieldNames.includes(field.apiName);

              return (
                <button
                  key={field.apiName}
                  disabled={isAlreadyAdded}
                  onClick={() => {
                    onSelect(field);
                    onClose();
                  }}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors ${
                    isAlreadyAdded
                      ? 'cursor-not-allowed opacity-40'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  {/* Required indicator */}
                  <div className="w-3 shrink-0">
                    {field.required && (
                      <Asterisk className="h-3 w-3 text-destructive" />
                    )}
                  </div>

                  {/* Field info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-xs font-medium text-foreground">
                        {field.displayLabel}
                      </p>
                      {isAlreadyAdded && (
                        <Badge variant="secondary" className="h-4 px-1 text-[9px]">
                          Added
                        </Badge>
                      )}
                    </div>
                    <p className="truncate text-[10px] text-muted-foreground font-mono">
                      {field.apiName}
                    </p>
                  </div>

                  {/* Data type badge */}
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {field.dataType}
                  </Badge>

                  {/* Picklist indicator */}
                  {field.pickListValues && field.pickListValues.length > 0 && (
                    <ListFilter className="h-3 w-3 shrink-0 text-muted-foreground" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
